/**
 * Supplier Form Component
 *
 * Form for creating and editing suppliers/vendors
 *
 * @module pages/suppliers/SupplierForm
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
} from 'react-bootstrap';
import { toast } from '../../utils/toast';
import {
  createSupplier,
  updateSupplier,
  getSupplier,
} from '../../services/supplierService';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';

// Payment terms options
const PAYMENT_TERMS_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'COD', label: 'Contra Entrega (COD)' },
  { value: 'Net 15', label: 'Neto 15 días' },
  { value: 'Net 30', label: 'Neto 30 días' },
  { value: 'Net 60', label: 'Neto 60 días' },
];

// Form validation schema
const supplierSchema = yup.object().shape({
  name: yup
    .string()
    .required('El nombre es requerido')
    .min(2, 'El nombre debe tener al menos 2 caracteres')
    .max(200, 'El nombre no puede exceder 200 caracteres'),
  contact_name: yup
    .string()
    .max(100, 'El nombre de contacto no puede exceder 100 caracteres')
    .nullable(),
  email: yup
    .string()
    .email('Ingresá un email válido')
    .max(100, 'El email no puede exceder 100 caracteres')
    .nullable(),
  phone: yup
    .string()
    .max(30, 'El teléfono no puede exceder 30 caracteres')
    .nullable(),
  address: yup
    .string()
    .max(300, 'La dirección no puede exceder 300 caracteres')
    .nullable(),
  tax_id: yup
    .string()
    .max(50, 'La cédula jurídica no puede exceder 50 caracteres')
    .nullable(),
  payment_terms: yup
    .string()
    .oneOf(['', 'COD', 'Net 15', 'Net 30', 'Net 60'], 'Término de pago inválido')
    .nullable(),
  notes: yup
    .string()
    .max(1000, 'Las notas no pueden exceder 1000 caracteres')
    .nullable(),
  is_active: yup.boolean(),
});

const SupplierForm = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: supplierId } = useParams();
  const { isAdmin, isTechnician } = useAuth();
  const isEditMode = !!supplierId;

  const [loading, setLoading] = useState(false);
  const [fetchingSupplier, setFetchingSupplier] = useState(isEditMode);
  const [error, setError] = useState(null);

  // React Hook Form
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: yupResolver(supplierSchema),
    defaultValues: {
      name: '',
      contact_name: '',
      email: '',
      phone: '',
      address: '',
      tax_id: '',
      payment_terms: '',
      notes: '',
      is_active: true,
    },
  });

  // Check permissions
  useEffect(() => {
    if (!isAdmin && !isTechnician) {
      toast.error('No tienes permisos para gestionar proveedores');
      navigate('/proveedores');
    }
  }, [isAdmin, isTechnician, navigate]);

  // Fetch supplier data if editing
  useEffect(() => {
    if (!isEditMode) return;

    const fetchSupplierData = async () => {
      try {
        setFetchingSupplier(true);
        const { data, error } = await getSupplier(supplierId);

        if (error) throw error;

        reset({
          name: data.name || '',
          contact_name: data.contact_name || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          tax_id: data.tax_id || '',
          payment_terms: data.payment_terms || '',
          notes: data.notes || '',
          is_active: data.is_active ?? true,
        });
      } catch (error) {
        console.error('Fetch supplier error:', error);
        setError(error.message);
        toast.error('Error al cargar el proveedor');
      } finally {
        setFetchingSupplier(false);
      }
    };

    fetchSupplierData();
  }, [supplierId, isEditMode, reset]);

  // Handle form submission
  const onSubmit = async (formData) => {
    try {
      setLoading(true);
      setError(null);

      // Clean empty strings to null
      const cleanData = {
        ...formData,
        contact_name: formData.contact_name || null,
        email: formData.email || null,
        phone: formData.phone || null,
        address: formData.address || null,
        tax_id: formData.tax_id || null,
        payment_terms: formData.payment_terms || null,
        notes: formData.notes || null,
      };

      let result;
      if (isEditMode) {
        result = await updateSupplier(supplierId, cleanData);
      } else {
        result = await createSupplier(cleanData);
      }

      if (result.error) throw result.error;

      toast.success(
        isEditMode
          ? 'Proveedor actualizado exitosamente'
          : 'Proveedor creado exitosamente'
      );

      queryClient.invalidateQueries(['suppliers']);
      queryClient.invalidateQueries(['suppliers-summary']);

      navigate('/proveedores');
    } catch (error) {
      console.error('Submit error:', error);
      setError(error.message);
      toast.error(
        isEditMode
          ? 'Error al actualizar el proveedor'
          : 'Error al crear el proveedor'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/proveedores');
  };

  if (fetchingSupplier) {
    return (
      <DashboardLayout>
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando proveedor...</span>
          </Spinner>
        </Container>
      </DashboardLayout>
    );
  }

  if (error && isEditMode && !loading) {
    return (
      <DashboardLayout>
        <Container className="mt-4">
          <Alert variant="danger">
            <Alert.Heading>Error al cargar el proveedor</Alert.Heading>
            <p>{error}</p>
            <Button variant="outline-danger" onClick={() => navigate('/proveedores')}>
              Volver a Proveedores
            </Button>
          </Alert>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Container fluid className="py-4">
        <Row className="justify-content-center">
          <Col lg={8} xl={7}>
            <Card className="shadow-sm">
              <Card.Header className="bg-primary text-white">
                <h4 className="mb-0">
                  <i className={`bi ${isEditMode ? 'bi-pencil-square' : 'bi-building-add'} me-2`}></i>
                  {isEditMode ? 'Editar Proveedor' : 'Nuevo Proveedor'}
                </h4>
              </Card.Header>
              <Card.Body>
                {error && (
                  <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit(onSubmit)}>
                  {/* Basic Information */}
                  <h6 className="text-muted mb-3">
                    <i className="bi bi-info-circle me-2"></i>
                    Información básica
                  </h6>

                  <Row>
                    {/* Name */}
                    <Col md={8}>
                      <Form.Group className="mb-3" controlId="name">
                        <Form.Label>
                          Nombre del Proveedor <span className="text-danger">*</span>
                        </Form.Label>
                        <Controller
                          name="name"
                          control={control}
                          render={({ field }) => (
                            <Form.Control
                              {...field}
                              type="text"
                              placeholder="Ej: EPA Costa Rica"
                              isInvalid={!!errors.name}
                              disabled={isSubmitting}
                            />
                          )}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.name?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* Tax ID */}
                    <Col md={4}>
                      <Form.Group className="mb-3" controlId="tax_id">
                        <Form.Label>Cédula jurídica</Form.Label>
                        <Controller
                          name="tax_id"
                          control={control}
                          render={({ field }) => (
                            <Form.Control
                              {...field}
                              type="text"
                              placeholder="Ej: 3-101-123456"
                              isInvalid={!!errors.tax_id}
                              disabled={isSubmitting}
                            />
                          )}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.tax_id?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Contact Information */}
                  <h6 className="text-muted mb-3 mt-4">
                    <i className="bi bi-person-lines-fill me-2"></i>
                    Información de contacto
                  </h6>

                  <Row>
                    {/* Contact Name */}
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="contact_name">
                        <Form.Label>Nombre del Contacto</Form.Label>
                        <Controller
                          name="contact_name"
                          control={control}
                          render={({ field }) => (
                            <Form.Control
                              {...field}
                              type="text"
                              placeholder="Ej: Juan Perez"
                              isInvalid={!!errors.contact_name}
                              disabled={isSubmitting}
                            />
                          )}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.contact_name?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* Phone */}
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="phone">
                        <Form.Label>Teléfono</Form.Label>
                        <Controller
                          name="phone"
                          control={control}
                          render={({ field }) => (
                            <Form.Control
                              {...field}
                              type="tel"
                              placeholder="Ej: 2222-3333"
                              isInvalid={!!errors.phone}
                              disabled={isSubmitting}
                            />
                          )}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.phone?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    {/* Email */}
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="email">
                        <Form.Label>Email</Form.Label>
                        <Controller
                          name="email"
                          control={control}
                          render={({ field }) => (
                            <Form.Control
                              {...field}
                              type="email"
                              placeholder="Ej: ventas@proveedor.com"
                              isInvalid={!!errors.email}
                              disabled={isSubmitting}
                            />
                          )}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.email?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* Payment Terms */}
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="payment_terms">
                        <Form.Label>Términos de pago</Form.Label>
                        <Controller
                          name="payment_terms"
                          control={control}
                          render={({ field }) => (
                            <Form.Select
                              {...field}
                              isInvalid={!!errors.payment_terms}
                              disabled={isSubmitting}
                            >
                              {PAYMENT_TERMS_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Form.Select>
                          )}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.payment_terms?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Address */}
                  <Form.Group className="mb-3" controlId="address">
                    <Form.Label>Dirección</Form.Label>
                    <Controller
                      name="address"
                      control={control}
                      render={({ field }) => (
                        <Form.Control
                          {...field}
                          as="textarea"
                          rows={2}
                          placeholder="Dirección física del proveedor..."
                          isInvalid={!!errors.address}
                          disabled={isSubmitting}
                        />
                      )}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.address?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {/* Additional Information */}
                  <h6 className="text-muted mb-3 mt-4">
                    <i className="bi bi-card-text me-2"></i>
                    Información adicional
                  </h6>

                  {/* Notes */}
                  <Form.Group className="mb-3" controlId="notes">
                    <Form.Label>Notas</Form.Label>
                    <Controller
                      name="notes"
                      control={control}
                      render={({ field }) => (
                        <Form.Control
                          {...field}
                          as="textarea"
                          rows={3}
                          placeholder="Notas adicionales sobre el proveedor..."
                          isInvalid={!!errors.notes}
                          disabled={isSubmitting}
                        />
                      )}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.notes?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {/* Active Status */}
                  <Form.Group className="mb-4" controlId="is_active">
                    <Controller
                      name="is_active"
                      control={control}
                      render={({ field: { onChange, value, ...field } }) => (
                        <Form.Check
                          {...field}
                          type="switch"
                          label={
                            <span>
                              Proveedor Activo
                              <small className="text-muted d-block">
                                Los proveedores inactivos no apareceran en selecciones de compras
                              </small>
                            </span>
                          }
                          checked={value}
                          onChange={(e) => onChange(e.target.checked)}
                          disabled={isSubmitting}
                        />
                      )}
                    />
                  </Form.Group>

                  {/* Action Buttons */}
                  <div className="d-flex justify-content-end gap-2">
                    <Button
                      variant="secondary"
                      onClick={handleCancel}
                      disabled={isSubmitting}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={isSubmitting || loading}
                    >
                      {isSubmitting || loading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          {isEditMode ? 'Actualizando...' : 'Creando...'}
                        </>
                      ) : (
                        <>
                          <i className={`bi ${isEditMode ? 'bi-check-lg' : 'bi-plus-lg'} me-2`}></i>
                          {isEditMode ? 'Actualizar Proveedor' : 'Crear Proveedor'}
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </DashboardLayout>
  );
};

export default SupplierForm;
